import { useCallback, useState } from "react";
import { useApiQuery } from "./useApi";
import { useTopic } from "./useConsoleSocket";
import { qk, TOPIC_FLEET_SUMMARY } from "@/lib/queryKeys";
import type { FleetSummaryFrame, OverviewPayload } from "@/types/fleet";

/**
 * The Overview payload, kept current by the `fleet.summary` topic.
 *
 * The HTTP call is the source of truth and the socket is a patch stream: the
 * server coalesces device health into `fleet.summary` on a 3-second window, so
 * frames carry deltas that are cheap to fold in. The 60 s poll underneath is the
 * safety net, and a `seq` gap on the topic refetches it outright (handled in the
 * socket provider) — so this hook never has to reason about missed frames.
 */
export function useFleetSummary() {
  const query = useApiQuery<OverviewPayload>(qk.overview, "/overview");
  const [patch, setPatch] = useState<
    Partial<OverviewPayload["devices"] & OverviewPayload["runs_in_flight"]> | null
  >(null);

  useTopic(
    TOPIC_FLEET_SUMMARY,
    useCallback((d: unknown) => {
      const frame = d as FleetSummaryFrame;
      if (frame?.counts) setPatch(frame.counts);
    }, []),
  );

  const base = query.data;
  // Optional chaining throughout: this hook feeds the Sidebar, which renders on every route, so a
  // server that adds or renames a field must degrade to a dash — not blank the entire console.
  const devices = base
    ? {
        total: patch?.total ?? base.devices?.total ?? 0,
        online: patch?.online ?? base.devices?.online ?? 0,
        stale: patch?.stale ?? base.devices?.stale ?? 0,
        offline: patch?.offline ?? base.devices?.offline ?? 0,
        unseen: patch?.unseen ?? base.devices?.unseen ?? 0,
        connected: base.devices?.connected ?? 0,
      }
    : null;

  const runs = base
    ? {
        running: patch?.running ?? base.runs_in_flight?.running ?? 0,
        waiting_unlock: base.runs_in_flight?.waiting_unlock ?? 0,
        queued: patch?.queued ?? base.runs_in_flight?.queued ?? 0,
        today_total: base.today?.runs ?? 0,
        today_success: base.today?.success ?? 0,
        today_failed: base.today?.failed ?? 0,
        // Server-computed over decided runs only, so a pending run never drags the rate down.
        today_success_rate: base.today?.success_rate ?? null,
      }
    : null;

  return { ...query, devices, runs, overview: base ?? null };
}

/** Just the counts the sidebar badge needs. */
export function useFleetPresence() {
  const { devices } = useFleetSummary();
  return devices;
}
